import React, { useEffect, useState } from 'react'
import { runInAction } from 'mobx'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import { observer } from 'mobx-react-lite'
import { Button, Popconfirm, Popover, Select, Space, Tag } from 'antd'
import { UserOutlined, InfoOutlined } from '@ant-design/icons'
import { useStores } from '../../../cls/stores/MobXProvider'
import {
	actLoadRequests,
	actUndoOrDeleteRequest
} from '../../../stores/ui-lst-requests/actions'

import {
	StatusTag,
	Filter,
	UserString,
	LstTable,
	CustomTable,
	FormatedTimestamp
} from '../../../components/Shared'
import {
	newRequest,
	requireRequestById,
	actLoadRequest,
	actSaveRequest
} from '../../../stores/requests/actions'
import ProgressModal from '../../../components/Form/ProgressModal'
import { DO_LOAD } from '../../../config'
import { sendNotification } from '../../../stores/notifications/action'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import dayjs from 'dayjs'
import { useSmartTranslation } from '../../../cls/TranslationContext'
/**
 * @component LstItem
 * @description Represents a single row in the Requests list table. Handles display, permissions, and UI interactions (view, edit, progress, delete/undo).
 *
 * @param {Object} props - The component props.
 * @param {string} props.id - The ID of the request item.
 * @param {boolean} [props.isFirst=false] - Indicates if this is the first item in the list (for targeting tour elements).
 * @param {boolean} [props.demo=false] - If true, skips loading the request data.
 *
 * @returns {JSX.Element} A table row representing a request.
 */
const LstItem = observer(({ id, isFirst, demo }) => {
	const { t } = useSmartTranslation()
	const { uiLstRequests, users } = useStores()
	const { loggedInUser } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin
	const request = requireRequestById(id)

	// Load request if not loaded
	useEffect(() => {
		if (request?.loaded === DO_LOAD && !demo) {
			actLoadRequest(request.id).catch(console.error)
		}
	}, [request])

	const selfPermission =
		request.createdBy === loggedInUser.user_id || isAdmin || isSuperAdmin

	// Delete is active only for the creator of the request if is in draft mode or if the user is an admin
	const deleteIsDisabled = !isAdmin
		? !(
				request.progress === 'not_read' &&
				(loggedInUser.user_id === request.createdBy ||
					loggedInUser.user_id === request.user_id)
		  )
		: false

	const [showConfirmUndoOrDelete, setConfirmUndoOrDelete] = useState(false)
	const confirmUndoOrDelete = () => {
		setConfirmUndoOrDelete(false)

		if (!!request.to_delete) {
			actUndoOrDeleteRequest(id, false).catch(console.error)
		} else {
			actUndoOrDeleteRequest(id, true).catch(console.error)
		}
	}

	const _actions = [
		{
			key: 'view',
			label: t('single.view'),
			value: 'view',
			disabled: !selfPermission && !users.canUser('read', 'requests')
		}
	]

	// give edit permission only the owner of the request
	if (selfPermission) {
		_actions.push({
			key: 'data',
			label: t('__edit%'),
			value: 'data',
			disabled: !selfPermission && !users.canUser('write', 'requests')
		})
	}

	// if user has permissions to change progress
	// (only if current user created the request or request created for this user)
	if (selfPermission || loggedInUser.user_id == request.for_user_id) {
		_actions.push({
			key: 'status',
			label: t('single.set-progress'),
			value: 'status',
			disabled: !selfPermission && !users.canUser('write', 'requests')
		})
	}

	// Delete / Undo actions
	if (selfPermission) {
		if (request.to_delete) {
			_actions.push({
				key: 'undo-del',
				label: t('single.delete-undo'),
				value: 'undo-delete',
				disabled:
					deleteIsDisabled && !users.canUser('delete', 'requests')
			})
		} else {
			_actions.push({
				key: 'del',
				label: t('single.delete'),
				value: 'delete',
				disabled:
					deleteIsDisabled && !users.canUser('delete', 'requests')
			})
		}
	}

	/**
	 * @function handleActions
	 * @description Handles user action selection (edit, view, progress, delete, undo) from dropdown menu in each request row.
	 *
	 * @param {string} value - The selected action value.
	 * @returns {void}
	 */
	const handleActions = (value) => {
		switch (value) {
			case 'data':
				runInAction(() => {
					uiLstRequests.setModal('request-modal', {
						visible: true,
						itmId: id,
						mode: 'data'
					})
				})
				break
			case 'view':
				runInAction(() => {
					uiLstRequests.setModal('request-modal', {
						visible: true,
						itmId: id,
						mode: 'view'
					})
				})
				break
			case 'undo-delete':
				setConfirmUndoOrDelete(true)
				break
			case 'delete':
				setConfirmUndoOrDelete(true)
				break
			case 'status':
				runInAction(() => {
					uiLstRequests.setModal('request-progress', {
						visible: true,
						itmId: id
					})
				})
				break
			default:
				break
		}
	}

	return (
		<tr>
			<td>
				<CustomTable
					data={[
						[
							{
								item: (
									<Space style={{ width: '100%' }}>
										<StatusTag
											id={
												isFirst
													? 'target-itm-priority'
													: ''
											}
											tag={request.priority}
											tagType={'PriorityTag'}
											style={{
												marginRight: 0,
												padding: '0 3px'
											}}
										/>
										<StatusTag
											id={
												isFirst
													? 'target-itm-progress'
													: ''
											}
											isShort={true}
											tag={request.progress}
											tagType={'RequestStatusTag'}
											style={{
												marginRight: 0,
												padding: '0 3px'
											}}
										/>
										<Button
											id={
												isFirst ? 'target-itm-link' : ''
											}
											type="link"
											onClick={() =>
												handleActions('view')
											}
										>
											{request.name}
										</Button>
									</Space>
								)
							}
						]
					]}
				/>
			</td>
			<td id={isFirst ? 'target-itm-owner' : ''}>
				{loggedInUser.user_id == request.createdBy ? (
					<UserOutlined />
				) : (
					<Tag color="default">
						<UserString userId={request.createdBy} path="name" />
					</Tag>
				)}
			</td>
			<td id={isFirst ? 'target-itm-user' : ''}>
				{loggedInUser.user_id == request.for_user_id ? (
					<UserOutlined />
				) : (
					<Tag color="default">
						{request.for_user_id && (
							<UserString
								userId={request.for_user_id}
								path="name"
							/>
						)}
					</Tag>
				)}
			</td>
			<td>
				<Space
					direction="vertical"
					align="end"
					style={{ width: '100%' }}
				>
					<Space align="end">
						<Popover
							content={() => (
								<CustomTable
									data={[
										[
											{
												item: (
													<strong>
														{t('single.info')}
													</strong>
												)
											}
										],
										[
											t('single.updated-last'),
											{
												item: (
													<FormatedTimestamp
														timestamp={
															request.modifiedTs
														}
														formatedAs="DD.MM.YYYY hh:mm"
													/>
												)
											}
										],
										[
											t('single.created-at'),
											{
												item: (
													<FormatedTimestamp
														timestamp={
															request.createdTs
														}
														formatedAs="DD.MM.YYYY hh:mm"
													/>
												)
											}
										]
									]}
								/>
							)}
							trigger="hover"
						>
							<Button
								id={isFirst ? 'target-itm-info' : ''}
								color="default"
								icon={<InfoOutlined />}
							/>
						</Popover>

						<Select
							value={t('single.actions')}
							options={_actions}
							onChange={handleActions}
							style={{ width: 120 }}
						/>

						<Popconfirm
							title={
								request.to_delete
									? t('single.confirm-undo-delete')
									: t('single.confirm-delete')
							}
							open={showConfirmUndoOrDelete}
							onConfirm={confirmUndoOrDelete}
							onCancel={() => setConfirmUndoOrDelete(false)}
							okType="link"
							placement="topLeft"
						/>
					</Space>
				</Space>
			</td>
		</tr>
	)
})
LstItem.propTypes = {
	id: PropTypes.string.isRequired
}
/**
 * @component RequestsLst
 * @description Displays a list of request items with filters, actions (edit, delete, progress), and a guided UI tour.
 * Includes ability-based visibility filtering and demo item support for the tour.
 *
 * @returns {JSX.Element} The full layout for managing and interacting with requests.
 */
const RequestsLst = observer(() => {
	const { t } = useSmartTranslation()
	const { uiLstRequests, users, tourStore } = useStores()
	const { loggedInUser, loggedInUserId } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const bySearch = uiLstRequests.search
	const byDel = uiLstRequests.del
	const byProgress = uiLstRequests.filter.progress
	const byPriority = uiLstRequests.filter.priority
	const for_user_uid = uiLstRequests.filter.for_user_uid
	const user_uid = uiLstRequests.filter.user_uid
	const orderBy = uiLstRequests.filter.orderBy
	const orderDir = uiLstRequests.filter.orderDir

	const [tour, setTour] = useState(false)

	useEffect(() => {
		actLoadRequests()
	}, [
		bySearch,
		byDel,
		byProgress,
		byPriority,
		for_user_uid,
		user_uid,
		orderBy,
		orderDir
	])

	// generate tour item that will be hidden based on the permissions
	const hideTourItems = []
	const hideFilters = []
	if (
		!(isAdmin || isSuperAdmin || users.canUser('delete_view', 'requests'))
	) {
		hideFilters.push('deleted')
		hideTourItems.push('delete')
	}

	if (!(isAdmin || isSuperAdmin || users.canUser('write', 'requests'))) {
		hideFilters.push('addNew')
		hideTourItems.push('new')
	}

	const requestLst = getTourArrayByKeys('requestLst', ['all'], hideTourItems)
	const tourFilter = getTourArrayByKeys(
		'filter',
		[
			'section',
			'search',
			'progress',
			'priority',
			'new',
			'toggleBtn',
			'ByUser',
			'ForUser',
			'orderBy',
			'delete'
		],
		hideTourItems
	)

	/**
	 * Start tour:
	 * This function starts the tour for the sprint form.
	 * It includes the general form, subtasks, comment and recurrence fields.
	 */
	const startTour = () => {
		runInAction(() => {
			// Open advance filter when we start tour
			uiLstRequests.filterExpanded = true
		})

		const hasItems = uiLstRequests.ids.length > 0
		if (!hasItems) {
			setTour(true)

			const newId = uuidv4()
			tourStore.setDeleteItemAct(() =>
				actUndoOrDeleteRequest(newId, true, true).catch(console.error)
			)

			const demoObj = {
				createdTs: dayjs().unix(),
				createdBy: loggedInUserId,
				for_user_id: loggedInUserId,
				name: 'demoItem',
				progress: 'not_read',
				comments: []
			}
			actSaveRequest(newId, demoObj, true).catch(console.error)
		}
		tourStore.startTour('RequestLst-tour', [...tourFilter, ...requestLst])
	}

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('__history%_request.multiple') }}
				buttonProps={{
					onClick: startTour
				}}
			/>
			<Filter
				module="requests"
				uiStore={uiLstRequests}
				addNew={() => {
					const newId = uuidv4()
					newRequest(newId).then(() => {
						runInAction(() => {
							uiLstRequests.setModal('request-modal', {
								visible: true,
								itmId: newId
							})
						})
					})
				}}
				hide={[...hideFilters, 'category']}
				progressOptions={[
					{
						key: 'by-progress',
						value: '',
						label: t('__by%single.status')
					},
					{
						key: 'by-progress-not-read',
						value: 'not_read',
						label: t('single.not-read')
					},
					{
						key: 'by-progress-completed',
						value: 'completed',
						label: t('single.completed')
					}
				]}
			/>
			<ProgressModal
				type="request"
				modalKey="request-progress"
				resetValue="not_read"
				uiStore={uiLstRequests}
				requireItemById={requireRequestById}
				loadItm={(id) => actLoadRequest(id)}
				saveItem={(itemId, itemValue) => {
					actSaveRequest(itemId, itemValue)

					// Create notification only if createdBy is not equal to for_user_id
					if (itemValue?.for_user_id != itemValue.createdBy) {
						const forUser = users.requireUserById(
							itemValue.for_user_id
						)
						sendNotification(
							loggedInUser,
							forUser?.email,
							itemValue?.for_user_id,
							'requests',
							itemId,
							'progress'
						)
					}
				}}
			/>
			<LstTable
				LstItem={LstItem}
				store={uiLstRequests}
				className="table-pro"
				demo={tour}
			>
				<tr id="items-lst">
					<th id="target-request">{t('single.requests')}</th>
					<th id="target-product-owner">
						{t('single.product-owner')}
					</th>
					<th id="target-request-for">{t('_request.for')}</th>
					<th id="target-actions">{t('_action.multiple')}</th>
				</tr>
			</LstTable>
		</GeneralContentWrapper>
	)
})
export default RequestsLst
